import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Sparkles, Eye, X } from "lucide-react";
import InfinityCaseDetail from "../../components/admin/InfinityCaseDetail";
import Pagination from "../../components/shared/Pagination";
import PaymentStatusBadge from "../../components/shared/PaymentStatusBadge";
import OrderStageBadge from "../../components/shared/OrderStageBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const ALL = "all"; // sentinel for "no filter" (Radix Select disallows empty value)

// Status filter only — the row badge is <OrderStageBadge>, which words `failed` and
// `cancelled` differently to these. This table stays worded the way an operator searches
// ("Payment Failed" is what they look for), and `match` is the raw statuses behind each
// option, which the API takes as a comma list.
const STAGES = [
  { key: "placed", label: "Order Placed", match: ["pending", "confirmed"] },
  { key: "submitted", label: "Order Submitted", match: ["processing"] },
  { key: "shipped", label: "Order Shipped", match: ["shipped", "delivered"] },
  { key: "failed", label: "Payment Failed", match: ["failed", "cancelled"] },
];

// Payment filter. Abandoned customer checkouts (still `pending`) are hidden by default
// server-side, because the order row is written before the card is charged (TR-25 item 3).
// Declined payments DO show by default — they are real orders needing attention (TR-26).
const PAYMENT_FILTERS = [
  { key: "paid_only", label: "Hide abandoned", params: {} },
  { key: "include_unpaid", label: "Include unpaid", params: { include_unpaid: "true" } },
  { key: "failed_only", label: "Failed only", params: { include_unpaid: "true", payment_status: "failed" } },
  { key: "pending_only", label: "Awaiting payment", params: { include_unpaid: "true", payment_status: "pending" } },
  // Orders taken over email/phone and recorded by an admin. They carry no line items and
  // a $0 value, so they are easy to mistake for broken rows in the full list.
  { key: "email_only", label: "Email orders only", params: { email_order: "true" } },
  { key: "exclude_email", label: "Exclude email orders", params: { email_order: "false" } },
];

export default function AdminInfinityOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caseModal, setCaseModal] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState(ALL);   // all | placed | submitted | shipped | failed
  const [payment, setPayment] = useState("paid_only");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Pagination (server-side — /admin/orders already takes page+limit)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchOrders = async (params) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, {
        params: { channel: "infinity", ...params },
      });
      setOrders(res.data?.orders || []);
      const pg = res.data?.pagination;
      setTotalItems(pg?.total ?? (res.data?.orders || []).length);
      setTotalPages(pg?.pages ?? 1);
    } catch (e) {
      toast.error("Failed to load Infinity orders");
    } finally {
      setLoading(false);
    }
  };

  const buildParams = () => {
    const params = { page, limit: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    // The table badges show the 3 stages, so the filter does too — each maps
    // to the raw statuses behind it, which the API takes as a comma list.
    if (stage !== ALL) {
      params.status = (STAGES.find((s) => s.key === stage)?.match || []).join(",");
    }
    Object.assign(params, PAYMENT_FILTERS.find((p) => p.key === payment)?.params || {});
    // Widen each end to the whole day, so "from = to" matches that day.
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      params.startDate = from.toISOString();
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      params.endDate = to.toISOString();
    }
    return params;
  };

  // A narrowed result set almost never has the page you were on, so go back to 1.
  useEffect(() => { setPage(1); }, [search, stage, payment, dateFrom, dateTo, pageSize]);

  // Re-query whenever any filter changes (search debounced together with the rest).
  useEffect(() => {
    const t = setTimeout(() => fetchOrders(buildParams()), 350);
    return () => clearTimeout(t);
  }, [search, stage, payment, dateFrom, dateTo, page, pageSize]);

  const hasFilters = search || stage !== ALL || payment !== "paid_only" || dateFrom || dateTo;
  const clearFilters = () => { setSearch(""); setStage(ALL); setPayment("paid_only"); setDateFrom(""); setDateTo(""); };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-infinity-orders">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-6 h-6 text-blue-600" /> Infinity Touch Orders</h2>
        <p className="text-slate-500">All Infinity Touch print orders. Open one to see the full case.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search by order #, customer or store" value={search} onChange={(e) => setSearch(e.target.value)} className="w-72" />
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={payment} onValueChange={setPayment}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Payment" /></SelectTrigger>
          <SelectContent>
            {PAYMENT_FILTERS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" aria-label="From date" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" aria-label="To date" />
        {hasFilters && (
          <Button variant="ghost" size="sm" className="text-slate-500" onClick={clearFilters}>
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Order #</TableHead>
              <TableHead className="font-semibold">Deceased</TableHead>
              <TableHead className="font-semibold">Funeral Home</TableHead>
              <TableHead className="font-semibold">Items</TableHead>
              <TableHead className="font-semibold">Total</TableHead>
              <TableHead className="font-semibold">Placed</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : orders.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                {hasFilters ? "No orders match these filters." : "No Infinity orders yet."}
              </TableCell></TableRow>
            ) : orders.map((o) => (
              <TableRow key={o.order_id} className="table-row cursor-pointer"
                onClick={() => o.memorial_record_id && setCaseModal({ recordId: o.memorial_record_id, focusOrderId: o.order_id })}>
                <TableCell className="font-medium">{o.order_number}</TableCell>
                <TableCell>{o.deceased_name || o.customer_name || "—"}</TableCell>
                <TableCell>{o.store_name || o.store_subdomain || o.store_id}</TableCell>
                <TableCell>{(o.items || []).length}</TableCell>
                <TableCell>{money(o.total_charged || o.total)}</TableCell>
                <TableCell>{fmtDate(o.created_at)}</TableCell>
                <TableCell>
                  {/* Payment first, then the fulfilment stage — the same order as the
                      case popup, so the two views read identically (TR-26 item 5).
                      Paid orders show only the stage badge; a declined order shows only
                      the payment badge, since it never entered fulfilment. */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {o.is_email_order && (
                      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Email Order</Badge>
                    )}
                    <PaymentStatusBadge status={o.payment_status} hidePaid />
                    <OrderStageBadge order={o} />
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="text-slate-600"
                    onClick={(e) => { e.stopPropagation(); if (o.memorial_record_id) setCaseModal({ recordId: o.memorial_record_id, focusOrderId: o.order_id }); }}>
                    <Eye className="w-4 h-4 mr-1" /> View details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && totalItems > 0 && (
          <div className="px-4 py-4 border-t">
            <Pagination page={page} totalPages={totalPages} onChange={setPage}
              totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} />
          </div>
        )}
      </div>

      {caseModal && (
        <InfinityCaseDetail open={!!caseModal} onClose={() => { setCaseModal(null); fetchOrders(buildParams()); }}
          memorialRecordId={caseModal.recordId} focusOrderId={caseModal.focusOrderId} />
      )}
    </div>
  );
}
